import {ResourceType} from "models/ResourceType.ts";

export class EditorStorage {
    #editor;
    #lessonId;
    #fileTypeConfig = {
        [ResourceType.NodeVideo]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "videoUploadSelectedGroup"
        },
        [ResourceType.NodeImage]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "imageUploadSelectedGroup"
        },
        [ResourceType.NodeAudio]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "audioUploadSelectedGroup"
        },
        [ResourceType.NodeInfoIcon]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "infoIconUploadSelectedGroup"
        }
    };

    constructor(editor, lessonId) {
        this.#editor = editor;
        this.#lessonId = lessonId;
        this.#editor.bind("changeNodeFilesList");
        this.#editor.bind("changeNodeFilesGroups");
        this.#editor.bind("removedNodeFile");
    }

    #validateFileType(fileType) {
        const config = this.#fileTypeConfig[fileType];
        if (!config) {
            console.error("Unhandled node file type!");
            return null;
        }
        return config;
    }

    #createSortedOptions(nodeFiles) {
        return nodeFiles
            .map(nodeFile => ({
                value: nodeFile.id,
                label: nodeFile.fileName,
                group: nodeFile.group ?? ""
            }))
            .sort((a, b) => a.group.localeCompare(b.group));
    }

    #getLocalStorageKey(fileType) {
        const config = this.#fileTypeConfig[fileType];
        return config && this.#lessonId
            ? `${config.localStoragePrefix}_${this.#lessonId}`
            : null;
    }

    #getLocalStorageSelectedGroup(fileType) {
        const key = this.#getLocalStorageKey(fileType);
        return key ? localStorage.getItem(key) : null;
    }

    #setLocalStorageSelectedGroup(selectedGroup, fileType) {
        const key = this.#getLocalStorageKey(fileType);
        if (key) {
            if (selectedGroup) {
                localStorage.setItem(key, selectedGroup);
            } else {
                localStorage.removeItem(key);
            }
        }
    }

    #getTempStorageSelectedGroup(fileType) {
        const config = this.#fileTypeConfig[fileType];
        return config?.tempSelectedGroup ?? null;
    }

    #setTempStorageSelectedGroup(selectedGroup, fileType) {
        const config = this.#fileTypeConfig[fileType];
        if (config) {
            config.tempSelectedGroup = selectedGroup;
        }
    }

    setNodeFiles(fileType, newNodeFiles) {
        const config = this.#validateFileType(fileType);
        if (!config) return;

        if (config.files !== newNodeFiles) {
            config.files = newNodeFiles;
            config.sortedOptions = this.#createSortedOptions(newNodeFiles);

            this.#editor.trigger("changeNodeFilesList", {
                fileType,
                data: newNodeFiles,
                sortedOptions: config.sortedOptions
            });
        }
    }

    setNodeFileGroups(fileType, newNodeFileGroups) {
        const config = this.#validateFileType(fileType);
        if (!config) return;

        if (config.groups !== newNodeFileGroups) {
            config.groups = newNodeFileGroups;
            this.#editor.trigger("changeNodeFilesGroups", {
                fileType,
                newGroups: newNodeFileGroups
            });
        }
    }

    setNodeFileRemoved(fileType, fileId) {
        const config = this.#validateFileType(fileType);
        if (!config) return;

        this.#editor.trigger("removedNodeFile", {fileType, fileId});
    }

    getSelectedGroup(fileType) {
        return this.#lessonId
            ? this.#getLocalStorageSelectedGroup(fileType)
            : this.#getTempStorageSelectedGroup(fileType);
    }

    saveSelectedGroup(fileType, selectedGroup) {
        if (this.#lessonId) {
            this.#setLocalStorageSelectedGroup(selectedGroup, fileType);
        } else {
            this.#setTempStorageSelectedGroup(selectedGroup, fileType);
        }
    }

    saveTempSelectedGroup(newLessonId) {
        Object.values(this.#fileTypeConfig).forEach(config => {
            if (config.tempSelectedGroup) {
                const key = `${config.localStoragePrefix}_${newLessonId}`;
                if (!localStorage.getItem(key)) {
                    localStorage.setItem(key, config.tempSelectedGroup);
                }
            }
        });
    }

    getSortedOptions(fileType) {
        return this.#fileTypeConfig[fileType]?.sortedOptions ?? [];
    }

    getGroups(fileType) {
        return this.#fileTypeConfig[fileType]?.groups ?? [];
    }
}